import { Button, DatePicker, Flex, Form, Link, Popup, Select } from '@approvalmax/ui/src/components';
import { constants } from 'modules/common';
import { memo } from 'react';
import { AccrualReportsBanner } from 'shared/components';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';

import { learnMoreLink } from './FormPopupContent.constants';
import { useAccrualAccountData, useFormData } from './FormPopupContent.hooks';
import { messages } from './FormPopupContent.messages';
import { TextArea } from './FormPopupContent.styles';
import { FormPopupContentProps } from './FormPopupContent.types';

export const FormPopupContent = memo<FormPopupContentProps>((props) => {
    const { reportConfig } = props;

    const { accrualAccounts, isLoadingAccrualAccounts, onAccrualAccountsInputChange } =
        useAccrualAccountData(reportConfig);
    const { form, isLoadingForm, handleSubmitForm } = useFormData(reportConfig, accrualAccounts);

    const company = useCompanyBasedOnUrl();

    return (
        <Form form={form} onSubmit={handleSubmitForm}>
            <Popup.Header
                title={messages.title}
                progress={isLoadingForm}
                actions={
                    <Button size='medium' color='blue80' disabled={isLoadingForm} type='submit'>
                        {messages.createButtonTitle}
                    </Button>
                }
            />

            <Popup.Body>
                <Flex container wrap spacing='16'>
                    <Flex size={24} spacing='4'>
                        {messages.description}
                        &nbsp;
                        <Link href={learnMoreLink} font='body' target='_blank'>
                            {messages.learnMoreText}
                        </Link>
                    </Flex>

                    <Flex size={12} direction='column' spacing='0'>
                        <TextArea
                            name='narration'
                            size='small'
                            fullHeight
                            required
                            multiline
                            label={messages.narrationFieldTitle}
                            placeholder={messages.narrationFieldPlaceholder}
                            disabled={isLoadingForm}
                            maxLength={constants.xeroConstants.manualJournalNarrationMaxLength}
                            clearable={false}
                        />
                    </Flex>

                    <Flex size={12} direction='column'>
                        <Flex block>
                            <DatePicker
                                name='date'
                                size='small'
                                required
                                label={messages.dateFieldTitle}
                                placeholder={messages.dateFieldPlaceholder}
                                clearable={false}
                                disabled={isLoadingForm}
                            />
                        </Flex>

                        <Flex block>
                            <Select
                                autocomplete
                                required
                                name='accrualAccountId'
                                size='small'
                                itemIdKey='fieldValueId'
                                itemNameKey='value'
                                items={accrualAccounts}
                                label={messages.accrualAccountFieldTitle}
                                placeholder={messages.accrualAccountFieldPlaceholder}
                                disabled={isLoadingForm}
                                progress={isLoadingAccrualAccounts}
                                onInputChange={onAccrualAccountsInputChange}
                            />
                        </Flex>
                    </Flex>
                </Flex>

                <AccrualReportsBanner company={company} withSpacing />
            </Popup.Body>
        </Form>
    );
});

FormPopupContent.displayName = 'FormPopupContent';
